import React from "react";
import { AppBar, Toolbar, IconButton, Typography, Button, Tooltip, Menu, MenuItem, Avatar, useTheme } from "@mui/material";
import { CloudUpload, PlayArrow, SaveAlt, Settings, HelpOutline, Brightness4, Brightness7, ExitToApp } from "@mui/icons-material";
import { getAuth, signOut } from "firebase/auth";
import { useThemeContext } from "./ThemeContext";

function IDPToolbar({ onUpload, onRunOCR, onExport }) {
  const auth = getAuth();
  const user = auth.currentUser;

  // Access theme context
  const { isDarkMode, toggleTheme } = useThemeContext();

  // Extract display name and initials
  const displayName = user?.displayName || "User";
  const initials = displayName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

  // User Menu State
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.clear();
      console.log("User signed out successfully.");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <AppBar 
      position="static"
      sx={{
        background: "#0d1b2a", // Semi-transparent for glass effect
        backdropFilter: "blur(10px)", // Glass effect
        borderBottom: "2px solid #00F5D4", // Neon glow bottom border
        boxShadow: "0px 0px 15px rgba(0, 245, 212, 0.5)", // Futuristic glow
      }}
    >
      <Toolbar>
        {/* App Title */}
        <Typography 
          variant="h6" 
          sx={{
            flexGrow: 1,
            fontWeight: "bold",
            color: "#00F5D4", // Neon text color
            letterSpacing: 1.5,
            //textTransform: "uppercase",
          }}
        >
          HealthXtract
        </Typography>

        {/* Upload Button */}
        <Tooltip title="Upload Document">
          <Button
            variant="outlined"
            component="label"
            sx={{
              color: "#00F5D4",
              borderColor: "#00F5D4",
              "&:hover": {
                backgroundColor: "rgba(0, 245, 212, 0.1)",
              }
            }}
            startIcon={<CloudUpload />}
          >
            Upload
            <input hidden type="file" onChange={onUpload} />
          </Button>
        </Tooltip>

        {/* Run OCR */}
        <Tooltip title="Run OCR">
          <IconButton sx={{ color: "#FFD700" }} onClick={onRunOCR}>
            <PlayArrow />
          </IconButton>
        </Tooltip>

        {/* Export Data */}
        <Tooltip title="Export Results">
          <IconButton sx={{ color: "#FF00FF" }} onClick={onExport}>
            <SaveAlt />
          </IconButton>
        </Tooltip>

        {/* Settings */}
        <Tooltip title="Settings">
          <IconButton sx={{ color: "#00FFFF" }}>
            <Settings />
          </IconButton>
        </Tooltip>

         {/* Theme Toggle */}
        <Tooltip title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}>
          <IconButton
            sx={{ color: isDarkMode ? "#FFD700" : "#00F5D4" }}
            onClick={toggleTheme}
          >
            {isDarkMode ? <Brightness7 /> : <Brightness4 />}
          </IconButton>
        </Tooltip>

        {/* Help */}
        <Tooltip title="Help">
          <IconButton sx={{ color: "#FF4500" }}>
            <HelpOutline />
          </IconButton>
        </Tooltip>

        {/* User Avatar */}
        <Tooltip title={displayName}>
          <IconButton onClick={handleMenuOpen}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: "#00F5D4", color: "#0d1b2a" }}>
              {initials}
            </Avatar>
          </IconButton>
        </Tooltip>

        {/* Dropdown Menu */}
        <Menu anchorEl={anchorEl} open={open} onClose={handleMenuClose}>
          <MenuItem disabled>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {displayName}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ color: "#FF4500" }}>
            <ExitToApp sx={{ marginRight: 1 }} /> Logout
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
}

export default IDPToolbar;
